/**
 * ArchivedSupervisorsPage — list archived supervisor records with restore + hard-delete.
 * Hard-delete requires typing 'DELETE' in a confirmation dialog.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, RotateCcw, Trash2, Search, FileText, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function recordName(r) {
  return r.full_name_ar
    || `${r.first_name || ''} ${r.last_name || ''}`.trim()
    || r.full_name_en
    || r.email
    || '—';
}

export default function ArchivedSupervisorsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // record to hard-delete
  const [confirmInput, setConfirmInput] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/archive/supervisors`, { headers: getAuthHeaders() });
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل تحميل الأرشيف' : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const restore = async (rec) => {
    setProcessingId(rec.id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/archive/supervisors/${rec.id}/restore`, {}, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم استعادة السجل' : 'Restored');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setProcessingId(null);
    }
  };

  const hardDelete = async () => {
    if (!confirmDelete || confirmInput.trim().toUpperCase() !== 'DELETE') return;
    setProcessingId(confirmDelete.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/archive/supervisors/${confirmDelete.id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم الحذف النهائي' : 'Permanently deleted');
      setConfirmDelete(null);
      setConfirmInput('');
      fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
      toast.error(msg, { duration: 6000 });
    } finally {
      setProcessingId(null);
    }
  };

  const visible = items.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [recordName(r), r.email, r.phone, r.id_number, r.employee_id, r.job_title_ar]
      .some((v) => v && String(v).toLowerCase().includes(q));
  });

  return (
    <DashboardLayout
      title="أرشيف المشرفين والموظفين"
      titleEn="Archived Supervisors & Staff"
      subtitle={language === 'ar' ? `${items.length} سجلّ مؤرشف` : `${items.length} archived records`}
      subtitleEn={`${items.length} archived records`}
      screenCode="ARC-003"
      backPath="/control/archive/asb606-admin"
      onBack={() => navigate('/control/archive/asb606-admin')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: 'ASB606 ADMIN', labelEn: 'ASB606 ADMIN', path: '/control/archive/asb606-admin' },
        { labelAr: 'المشرفين والموظفين', labelEn: 'Supervisors & Staff' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="archived-supervisors-page">
        {/* Search */}
        <div className="relative max-w-md mb-4">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'بحث بالاسم/البريد/الهوية...' : 'Search by name/email/ID...'}
            className="w-full ps-3 pe-9 py-2.5 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-amber-500/50 focus:outline-none"
            data-testid="archived-sup-search"
          />
        </div>

        {/* List */}
        <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400 mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-[#808080]">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{language === 'ar' ? 'لا توجد سجلّات مؤرشفة' : 'No archived records'}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#2a2a4e]/50">
              {visible.map((rec) => (
                <div
                  key={rec.id}
                  className="p-4 flex items-center justify-between gap-3 hover:bg-[#0a0a0a]/40"
                  data-testid={`archived-sup-row-${rec.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium">{recordName(rec)}</span>
                      {rec.employee_id && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/10 text-[#d4af37]">
                          ID: {rec.employee_id}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#808080] mt-1 flex items-center gap-2 flex-wrap">
                      {rec.email && <span>{rec.email}</span>}
                      {rec.phone && <span dir="ltr">{rec.phone}</span>}
                      {rec.archived_at && (
                        <span className="text-amber-400">
                          {language === 'ar' ? 'أُرشف:' : 'archived:'} {new Date(rec.archived_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {rec.archived_reason && (
                      <p className="text-xs text-[#606060] mt-1">
                        {language === 'ar' ? 'السبب: ' : 'Reason: '}{rec.archived_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => restore(rec)}
                      disabled={processingId === rec.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                      data-testid={`restore-sup-${rec.id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 me-1" />
                      {language === 'ar' ? 'استعادة' : 'Restore'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setConfirmDelete(rec); setConfirmInput(''); }}
                      disabled={processingId === rec.id}
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8"
                      data-testid={`harddel-sup-${rec.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 me-1" />
                      {language === 'ar' ? 'حذف نهائي' : 'Delete'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Hard-delete confirm dialog (type DELETE) */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setConfirmInput(''); } }}
      >
        <DialogContent className="bg-[#1a1a2e] border-red-500/30 text-white" data-testid="harddel-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {language === 'ar' ? 'تأكيد الحذف النهائي' : 'Confirm permanent delete'}
            </DialogTitle>
            <DialogDescription className="text-[#a0a0a0]">
              {language === 'ar'
                ? <>سيتم حذف <span className="text-white">{confirmDelete && recordName(confirmDelete)}</span> نهائياً من قاعدة البيانات ولا يمكن استعادته. اكتب <code className="text-red-400 font-mono mx-1">DELETE</code> للتأكيد.</>
                : <>This will permanently delete <span className="text-white">{confirmDelete && recordName(confirmDelete)}</span> and cannot be undone. Type <code className="text-red-400 font-mono mx-1">DELETE</code> to confirm.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="DELETE"
              className="bg-[#0a0a0a] border-red-500/30 text-white font-mono"
              data-testid="harddel-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setConfirmDelete(null); setConfirmInput(''); }}
              className="border-[#2a2a4e] text-white"
              data-testid="harddel-cancel"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={hardDelete}
              disabled={confirmInput.trim().toUpperCase() !== 'DELETE' || processingId === confirmDelete?.id}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="harddel-confirm-btn"
            >
              {processingId === confirmDelete?.id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Trash2 className="h-4 w-4 me-2" />{language === 'ar' ? 'حذف نهائي' : 'Delete forever'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
